import { useCallback, useEffect, useRef, useState, type MutableRefObject, type RefObject } from 'react';
import { useAiSelectionStore } from '../../../terminal/store/aiSelectionStore';
import { selectionRangeKey } from '../utils/editorSelectionUtils';
import { findSegmentAtOffset, findSegmentCoveredBySelection, offsetToLineColumn } from '../utils/statementAtCursor';
import type { StatementSegmentsSnapshot } from './useStatementSegments';
import { isLikelyCompleteStatement, isPencilReachableStatement } from '../../common/utils/peekResultsDetection';
import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { QueryEditorHandle } from '../components/QueryEditor';

export type CursorStatement = {
  range: EditorSelectionRange;
  text: string;
  multiStatement: boolean;
  /** Buffer offset of the statement's first char — seeds Open-full VQE. */
  startOffset: number;
};

export type RawCursorSegment = CursorStatement & { endOffset: number };

type UseCursorStatementTrackingParams = {
  tabId: string;
  tabTitle: string;
  engineId: DatabaseEngineId;
  editorRef: RefObject<QueryEditorHandle | null>;
  /**
   * Version-keyed statement-segments getter (`useStatementSegments`). Caret
   * moves through an unchanged buffer are a cache hit: no `getValue()`, no
   * re-parse. Owned by the tab so the F5 shortcut path shares the same cache.
   */
  getStatementSegments: () => StatementSegmentsSnapshot | null;
  /** Closes the peek panel when the selection changes (set later by usePeekResults). */
  closePeekRef: MutableRefObject<(() => void) | null>;
};

/**
 * Tracks the current editor selection and the statement under the cursor for
 * a query editor tab. The selection drives the floating Execute/Peek bars and
 * the AI selection mirror (`aiSelectionStore`); `currentCursorStatement`
 * (pencil-reachable gate) drives the Monaco highlight, F5 narrowing, and the
 * edit pencil; `rawCursorSegment` (ungated, with absolute offsets) lets the
 * parse-error affordance attach to BROKEN statements (EDG-11..15). See the
 * inline comments — all gating semantics are carried verbatim from the
 * previous inline implementation.
 */
export function useCursorStatementTracking({
  tabId,
  tabTitle,
  engineId,
  editorRef,
  getStatementSegments,
  closePeekRef,
}: UseCursorStatementTrackingParams) {
  // Track the currently selected text + range — drives the floating
  // selection action buttons (Execute + Peek) rendered over the editor and
  // the gutter buttons' selection mode.
  const [editorSelectedText, setEditorSelectedText] = useState('');
  const [editorSelectedRange, setEditorSelectedRange] =
    useState<EditorSelectionRange | null>(null);
  const setTabSelection = useAiSelectionStore((s) => s.setTabSelection);
  const removeTabSelection = useAiSelectionStore((s) => s.removeTabSelection);
  const lastSelectionRef = useRef<{ key: string | null; text: string } | null>(null);

  // Track the statement under the cursor whenever there's no active selection.
  // Drives the subtle "current statement" Monaco decoration, the Execute|Peek
  // floating bar, AND the minimal visual-edit (pencil) affordance.
  //
  // The gate is `isPencilReachableStatement` (structural sanity only), NOT the
  // stricter `isLikelyCompleteStatement`: the pencil is driven by the real
  // parse (see `useCursorStatementEditInfo` / `cursorEditInfo` in the host) and
  // must not be starved when a complete statement merely ends in a word the
  // bar's trailing-continuation heuristic treats as mid-composition (`… IS
  // NULL`, `… FETCH FIRST n ROWS ONLY`, `… OFFSET n ROWS`). The bar's
  // flash-suppression stays strict *inside* `CursorStatementActions` /
  // `CursorStatementGutterActions` (they re-check `isLikelyCompleteStatement`
  // on the statement text), and the strict gate is re-applied for the two
  // consumers that must NOT loosen — the Monaco highlight (below) and F5
  // statement-narrowing (in the host).
  //
  // `multiStatement` records whether the buffer has 2+ statements: the
  // Execute/Peek bars only render for multi-statement buffers (single-statement
  // buffers run via the toolbar / F5), but the edit pencil renders for the
  // cursor statement in ALL buffers, single-statement included.
  const [currentCursorStatement, setCurrentCursorStatement] =
    useState<CursorStatement | null>(null);
  const lastCursorStatementKeyRef = useRef<string | null>(null);

  // The RAW segment under the cursor — the ungated sibling of
  // `currentCursorStatement` (which only populates when
  // `isPencilReachableStatement` passes: balanced parens, no trailing operator).
  // The EDG-11..15 parse-error finding shapes (`… WHERE (Color =`) fail that
  // gate, so `currentCursorStatement` is null for them and the pencil-path chip
  // could never fire. This carries the segment regardless of pencil-reachability
  // (with absolute offsets for tree-sitter overlap) so the never-silent
  // parse-error affordance can attach to a BROKEN statement. It never lights the
  // pencil on its own — the affordance only uses it once a syntax-error signal
  // (marker OR the conservative local tree-sitter predicate) confirms the
  // segment is broken (see `brokenCursorStatement` in the host).
  const [rawCursorSegment, setRawCursorSegment] =
    useState<RawCursorSegment | null>(null);
  const lastRawSegmentKeyRef = useRef<string | null>(null);

  // The statement a selection covers exactly (`findSegmentCoveredBySelection`),
  // pencil-reachable — the edit pencil's target while text is selected, when
  // `currentCursorStatement` is null. A partial selection gets none: Execute /
  // Peek run just the selected text there, and an Edit that took the whole
  // statement would act on something other than what is selected.
  const [selectedStatement, setSelectedStatement] =
    useState<CursorStatement | null>(null);
  const lastSelectedStatementKeyRef = useRef<string | null>(null);

  // Mirror selection + cursor-statement state into refs so the F5 keyboard
  // handler (in the host) can read the latest values without re-subscribing to
  // `window.keydown` on every keystroke / cursor move.
  const editorSelectedTextRef = useRef('');
  editorSelectedTextRef.current = editorSelectedText;
  const currentCursorStatementRef = useRef(currentCursorStatement);
  currentCursorStatementRef.current = currentCursorStatement;

  const handleEditorSelectionChange = useCallback(
    ({
      range,
      selectedText,
      cursorPosition,
    }: {
      range: EditorSelectionRange | null;
      selectedText: string;
      cursorPosition: { lineNumber: number; column: number; offset: number } | null;
    }) => {
      const key = selectionRangeKey(range);
      const last = lastSelectionRef.current;

      const selectionChanged = !last || last.key !== key || last.text !== selectedText;

      if (selectionChanged) {
        lastSelectionRef.current = { key, text: selectedText };
        setEditorSelectedText(selectedText);
        setEditorSelectedRange(range);

        setTabSelection({
          tabId,
          tabName: tabTitle,
          selection: range,
          selectedText,
          updatedAt: Date.now(),
        });

        // Close peek panel when selection changes (new selection or cleared)
        closePeekRef.current?.();
      }

      // Current-statement tracking — only when there's NO active selection and
      // the segment at the cursor passes `isPencilReachableStatement` (structural
      // sanity: executable shape, balanced parens, no trailing operator). This
      // is deliberately LOOSER than the bar's `isLikelyCompleteStatement` gate so
      // the pencil (driven by the real parse downstream) isn't starved for
      // common complete endings the bar heuristic treats as mid-composition
      // (`… IS NULL`, `… ROWS ONLY`, `… OFFSET n ROWS`). The bar's run-action
      // flash-suppression stays strict inside CursorStatement(Gutter)Actions.
      //
      // We populate this for buffers with >= 1 statement (single-statement
      // included) because the edit pencil affordance must appear for the cursor
      // statement in ALL buffers. `multiStatement` is recorded so the cursor
      // Execute/Peek bars can stay 2+-only (they read it via props) while the
      // pencil renders regardless.
      const hasSelection = !!range;
      let nextStatement: CursorStatement | null = null;
      let nextSelected: CursorStatement | null = null;
      let nextRawSegment: RawCursorSegment | null = null;
      // Only caret-only updates pay for the buffer + parse — and via the
      // segments cache they only pay at all when the buffer changed since the
      // last look (caret navigation through an unchanged document is a cache
      // hit: no getValue(), no parse).
      const parsed = !hasSelection && cursorPosition ? getStatementSegments() : null;
      if (parsed && cursorPosition && parsed.segments.length >= 1) {
        const { sql, segments } = parsed;
        const statementCount = segments.length;
        const seg = findSegmentAtOffset(segments, cursorPosition.offset);
        if (seg) {
          const startPos = offsetToLineColumn(sql, seg.trimStart);
          const endPos = offsetToLineColumn(sql, seg.trimEnd);
          const segRange = {
            startLineNumber: startPos.lineNumber,
            startColumn: startPos.column,
            endLineNumber: endPos.lineNumber,
            endColumn: endPos.column,
          };
          const multiStatement = statementCount >= 2;
          // Always track the raw segment (parse-error affordance for BROKEN,
          // non-pencil-reachable statements — EDG-11..15).
          nextRawSegment = {
            range: segRange,
            text: seg.text,
            multiStatement,
            startOffset: seg.trimStart,
            endOffset: seg.trimEnd,
          };
          // The pencil path stays gated exactly as before (BUG-4).
          if (isPencilReachableStatement(seg.text, engineId)) {
            nextStatement = {
              range: segRange,
              text: seg.text,
              multiStatement,
              startOffset: seg.trimStart,
            };
          }
        }
      }
      const selectionTrimmed = selectedText.trim();
      const parsedForSelection = range && cursorPosition && selectionTrimmed ? getStatementSegments() : null;
      if (range && cursorPosition && parsedForSelection) {
        const { sql, segments } = parsedForSelection;
        // The caret sits on the selection's active end, so its offset gives the
        // selection's start without scanning the buffer.
        const caretAtEnd =
          cursorPosition.lineNumber === range.endLineNumber && cursorPosition.column === range.endColumn;
        const selectionStart = caretAtEnd ? cursorPosition.offset - selectedText.length : cursorPosition.offset;
        const start = selectionStart + (selectedText.length - selectedText.trimStart().length);
        const seg = findSegmentCoveredBySelection(segments, start, start + selectionTrimmed.length);
        if (seg && isPencilReachableStatement(seg.text, engineId)) {
          const startPos = offsetToLineColumn(sql, seg.trimStart);
          const endPos = offsetToLineColumn(sql, seg.trimEnd);
          nextSelected = {
            range: {
              startLineNumber: startPos.lineNumber,
              startColumn: startPos.column,
              endLineNumber: endPos.lineNumber,
              endColumn: endPos.column,
            },
            text: seg.text,
            multiStatement: segments.length >= 2,
            startOffset: seg.trimStart,
          };
        }
      }
      const nextSelectedKey = nextSelected ? `${nextSelected.startOffset}|${nextSelected.text.length}` : null;
      if (lastSelectedStatementKeyRef.current !== nextSelectedKey) {
        lastSelectedStatementKeyRef.current = nextSelectedKey;
        setSelectedStatement(nextSelected);
      }
      const nextKey = nextStatement
        ? `${nextStatement.range.startLineNumber}:${nextStatement.range.startColumn}-${nextStatement.range.endLineNumber}:${nextStatement.range.endColumn}|${nextStatement.text.length}|${nextStatement.multiStatement ? 'm' : 's'}`
        : null;
      if (lastCursorStatementKeyRef.current !== nextKey) {
        lastCursorStatementKeyRef.current = nextKey;
        setCurrentCursorStatement(nextStatement);
      }
      const nextRawKey = nextRawSegment
        ? `${nextRawSegment.range.startLineNumber}:${nextRawSegment.range.startColumn}-${nextRawSegment.range.endLineNumber}:${nextRawSegment.range.endColumn}|${nextRawSegment.text.length}|${nextRawSegment.multiStatement ? 'm' : 's'}`
        : null;
      if (lastRawSegmentKeyRef.current !== nextRawKey) {
        lastRawSegmentKeyRef.current = nextRawKey;
        setRawCursorSegment(nextRawSegment);
      }
    },
    [tabTitle, setTabSelection, tabId, engineId, getStatementSegments, closePeekRef]
  );

  // Push the "current statement" highlight down to Monaco as a decoration.
  // Monaco's cursor-selection event fires on every keystroke (typing moves the
  // caret), so `currentCursorStatement` is kept fresh by `handleEditorSelectionChange`
  // without a separate content-change listener.
  //
  // The highlight keeps the STRICTER `isLikelyCompleteStatement` gate (the
  // pencil path loosened `currentCursorStatement` to `isPencilReachableStatement`):
  // a prominent full-statement decoration should not appear while the user is
  // still typing a trailing clause (`SELECT * FROM ` / `WHERE x IS`), even
  // though the subtle pencil may.
  useEffect(() => {
    const stmt = currentCursorStatement;
    const highlightRange =
      stmt && isLikelyCompleteStatement(stmt.text, engineId) ? stmt.range : null;
    editorRef.current?.setCurrentStatementHighlight(highlightRange);
    return () => {
      editorRef.current?.setCurrentStatementHighlight(null);
    };
  }, [currentCursorStatement, engineId, editorRef]);

  useEffect(() => {
    return () => {
      removeTabSelection(tabId);
    };
  }, [removeTabSelection, tabId]);

  return {
    editorSelectedText,
    editorSelectedRange,
    currentCursorStatement,
    rawCursorSegment,
    selectedStatement,
    editorSelectedTextRef,
    currentCursorStatementRef,
    handleEditorSelectionChange,
  };
}
